/**
 * Generate personalized practice suggestions based on user's skill level and recent activity
 */

export const getSuggestions = (skillLevel, recentActivities = [], userSettings = {}) => {
    const suggestions = [];

    // Level 1 (Learning) Suggestions
    if (skillLevel === 1) {
        suggestions.push({
            id: 'koch-progress',
            icon: '📚',
            title: 'Continue Koch Method',
            description: 'Master the fundamentals with guided lessons',
            action: 'Start Guided Lessons',
            gameMode: 'guided',
            priority: 1
        });

        suggestions.push({
            id: 'character-sets',
            icon: '🔤',
            title: 'Practice Character Recognition',
            description: 'Build familiarity with letters and numbers',
            action: 'Practice Characters',
            gameMode: 'letters',
            priority: 2
        });

        suggestions.push({
            id: 'slow-speed',
            icon: '🐌',
            title: 'Start Slow, Build Steady',
            description: 'Begin at 10-15 WPM and gradually increase',
            action: 'Adjust Speed',
            view: 'settings/audio',
            priority: 3
        });
    }

    // Level 2 (Improving) Suggestions
    if (skillLevel === 2) {
        suggestions.push({
            id: 'callsigns',
            icon: '📻',
            title: 'Practice Real Call Signs',
            description: 'Get comfortable copying actual ham radio formats',
            action: 'Practice Call Signs',
            gameMode: 'callsigns',
            priority: 1
        });

        suggestions.push({
            id: 'q-codes',
            icon: '❓',
            title: 'Learn Q-Codes',
            description: 'Master essential operating abbreviations',
            action: 'Practice Q-Codes',
            gameMode: 'qcodes',
            priority: 2
        });

        suggestions.push({
            id: 'increase-wpm',
            icon: '⚡',
            title: 'Aim for 20+ WPM',
            description: 'Push your speed to prepare for advanced modes',
            action: 'Speed Training',
            view: 'settings/audio',
            priority: 3
        });

        suggestions.push({
            id: 'daily-practice',
            icon: '📅',
            title: 'Build Your Streak',
            description: 'Practice daily to build muscle memory',
            action: 'Start Session',
            gameMode: 'guided',
            priority: 4
        });
    }

    // Level 3 (Mastery) Suggestions
    if (skillLevel === 3) {
        suggestions.push({
            id: 'qso-practice',
            icon: '💬',
            title: 'Simulated QSOs',
            description: 'Practice realistic on-air conversations',
            action: 'Start QSO',
            gameMode: 'qso',
            priority: 1
        });

        suggestions.push({
            id: 'contests',
            icon: '🏆',
            title: 'Contest Simulation',
            description: 'Train for high-speed competitive operating',
            action: 'Enter Contest',
            gameMode: 'contests',
            priority: 2
        });

        suggestions.push({
            id: 'pileup',
            icon: '📡',
            title: 'Pileup Simulator',
            description: 'Challenge yourself with multiple simultaneous signals',
            action: 'Start Pileup',
            gameMode: 'pileup',
            priority: 3
        });

        suggestions.push({
            id: 'target-30wpm',
            icon: '🎯',
            title: 'Target 30+ WPM',
            description: 'Achieve minimum competitive speed',
            action: 'Speed Challenge',
            view: 'settings/audio',
            priority: 4
        });
    }

    // Level 4 (Head Copy) Suggestions
    if (skillLevel === 4) {
        suggestions.push({
            id: 'icr-words',
            icon: '⚡',
            title: 'Head Copy Training',
            description: 'Train your brain to recognize entire words instantly',
            action: 'Head Copy Words',
            gameMode: 'icr_words',
            priority: 1
        });

        suggestions.push({
            id: 'comprehension',
            icon: '📖',
            title: 'Comprehension Training',
            description: 'Copy full sentences and understand meaning in real-time',
            action: 'Start Comprehension',
            gameMode: 'icr_comprehension',
            priority: 2
        });

        suggestions.push({
            id: 'advanced-contests',
            icon: '🥇',
            title: 'Advanced Contesting',
            description: 'Master high-speed exchanges and pile-up work',
            action: 'Contest Mode',
            gameMode: 'contests',
            priority: 3
        });

        suggestions.push({
            id: '40-wpm',
            icon: '🚀',
            title: 'Push to 40+ WPM',
            description: 'Reach expert-level speeds',
            action: 'Speed Training',
            view: 'settings/audio',
            priority: 4
        });
    }

    // Activity-based suggestions (apply to all levels)
    if (recentActivities.length > 0) {
        const avgAccuracy = calculateAverageAccuracy(recentActivities);
        const avgWpm = calculateAverageWPM(recentActivities);

        if (avgAccuracy < 80) {
            suggestions.push({
                id: 'accuracy-focus',
                icon: '🎯',
                title: 'Focus on Accuracy',
                description: `Your recent accuracy is ${avgAccuracy.toFixed(0)}%. Slow down and aim for 90%+`,
                action: 'Accuracy Practice',
                view: 'settings/audio',
                priority: 0 // High priority
            });
        }

        if (avgWpm < 15 && skillLevel >= 2) {
            suggestions.push({
                id: 'speed-building',
                icon: '⏱️',
                title: 'Build Your Speed',
                description: `Current average: ${avgWpm} WPM. Try gradual speed increases`,
                action: 'Speed Drills',
                view: 'settings/audio',
                priority: 0
            });
        }
    }

    // Streak-based suggestions
    const streak = userSettings?.streak || 0;
    if (streak === 0) {
        suggestions.push({
            id: 'start-streak',
            icon: '🔥',
            title: 'Start Your Streak',
            description: 'Practice today to begin building consistency',
            action: 'Start Practice',
            gameMode: 'guided',
            priority: 2
        });
    } else if (streak >= 7) {
        suggestions.push({
            id: 'maintain-streak',
            icon: '🔥',
            title: `${streak}-Day Streak!`,
            description: 'Keep it going! Consistency builds skill',
            action: 'Practice Today',
            gameMode: 'guided',
            priority: 2
        });
    }

    // Sort by priority (0 = highest)
    return suggestions.sort((a, b) => a.priority - b.priority);
};

// Helper functions
const calculateAverageAccuracy = (activities) => {
    if (!activities || activities.length === 0) return 100;
    const total = activities.reduce((sum, a) => sum + (a.accuracy || 0), 0);
    return total / activities.length;
};

const calculateAverageWPM = (activities) => {
    if (!activities || activities.length === 0) return 0;
    const total = activities.reduce((sum, a) => sum + (a.wpm || 0), 0);
    return total / activities.length;
};

export default getSuggestions;
